
import React from "react";
import { Search, Gamepad2, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface MentoriaFiltersProps {
  searchTerm: string;
  selectedGameFilter: string | null;
  selectedTypeFilter: 'all' | 'individual' | 'group';
  games: Array<{ id: string; name: string }>;
  onSearchChange: (value: string) => void;
  onGameFilterChange: (gameId: string | null) => void;
  onTypeFilterChange: (type: 'all' | 'individual' | 'group') => void;
}

export const MentoriaFilters: React.FC<MentoriaFiltersProps> = ({
  searchTerm,
  selectedGameFilter,
  selectedTypeFilter,
  games,
  onSearchChange,
  onGameFilterChange,
  onTypeFilterChange,
}) => {
  return (
    <div className="mb-8 space-y-6">
      {/* Search */}
      <div className="relative max-w-lg mx-auto">
        <Search className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
        <Input
          placeholder="Buscar mentorias..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 bg-[#1A2233] border-[#2D3748] text-white py-6"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        {/* Game Filter */}
        <div className="space-y-2">
          <h3 className="text-[#00B6F0] font-semibold text-sm flex items-center gap-2">
            <Gamepad2 className="h-4 w-4" /> 
            Jogos
          </h3>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={!selectedGameFilter ? "default" : "outline"}
              className={`cursor-pointer ${
                !selectedGameFilter 
                  ? "bg-[#00B6F0] text-black" 
                  : "bg-[#1A2233] text-gray-300 border-[#2D3748]"
              }`}
              onClick={() => onGameFilterChange(null)}
            >
              Todos
            </Badge>
            
            {games.map(game => (
              <Badge
                key={game.id}
                variant={selectedGameFilter === game.id ? "default" : "outline"}
                className={`cursor-pointer ${
                  selectedGameFilter === game.id
                    ? "bg-[#00B6F0] text-black"
                    : "bg-[#1A2233] text-gray-300 border-[#2D3748]"
                }`}
                onClick={() => onGameFilterChange(game.id)}
              >
                {game.name}
              </Badge>
            ))}
          </div>
        </div>

        {/* Session Type Filter */}
        <div className="space-y-2">
          <h3 className="text-[#00B6F0] font-semibold text-sm flex items-center gap-2">
            <Users className="h-4 w-4" /> 
            Tipo de Sessão
          </h3>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={selectedTypeFilter === "all" ? "default" : "outline"}
              className={`cursor-pointer ${
                selectedTypeFilter === "all"
                  ? "bg-[#00B6F0] text-black" 
                  : "bg-[#1A2233] text-gray-300 border-[#2D3748]"
              }`}
              onClick={() => onTypeFilterChange("all")}
            >
              Todas
            </Badge>
            <Badge
              variant={selectedTypeFilter === "individual" ? "default" : "outline"}
              className={`cursor-pointer ${
                selectedTypeFilter === "individual"
                  ? "bg-green-700 text-green-100 border-green-500" 
                  : "bg-[#1A2233] text-gray-300 border-[#2D3748]"
              }`}
              onClick={() => onTypeFilterChange("individual")}
            >
              Individual
            </Badge>
            <Badge
              variant={selectedTypeFilter === "group" ? "default" : "outline"}
              className={`cursor-pointer ${
                selectedTypeFilter === "group"
                  ? "bg-purple-700 text-purple-100 border-purple-500" 
                  : "bg-[#1A2233] text-gray-300 border-[#2D3748]"
              }`}
              onClick={() => onTypeFilterChange("group")}
            >
              Em Grupo
            </Badge>
          </div>
        </div>
        
        {/* Clear Filters Button - Only show when filters are applied */}
        {(selectedGameFilter || selectedTypeFilter !== 'all' || searchTerm) && (
          <Button
            variant="link"
            className="text-[#00B6F0] px-0 h-auto self-end"
            onClick={() => {
              onGameFilterChange(null);
              onTypeFilterChange('all');
              onSearchChange('');
            }}
          >
            Limpar filtros
          </Button>
        )}
      </div>
    </div>
  );
};
